import Papa from "papaparse";
import { ImportFileType } from "../../const/file-type";
import { Delimiters } from "../../const/delimiters";

const ROWS_TO_PREVIEW = 10;
const MAX_ROWS_TO_SCAN = 1000;

function isRowValid(row) {
  return row && row.filter((field) => field !== "").length > 0;
}

function isFieldFilled(value) {
  return value !== undefined && value !== null && String(value).trim() !== "";
}

function getFilledColumnIndices(row) {
  const indices = new Set();
  for (let i = 0; i < row.length; i++) {
    if (isFieldFilled(row[i])) {
      indices.add(i);
    }
  }
  return indices;
}

function selectRowsWithMaxColumnCoverage(allRows, maxRows, hasHeader) {
  if (allRows.length === 0) {
    return [];
  }

  const startIndex = hasHeader ? 1 : 0;
  const columnCount = allRows[0]?.length || 0;
  const coveredColumns = new Set();
  const selectedRows = [];

  if (hasHeader) {
    selectedRows.push(allRows[0]);
  }

  const rowsToScan = Math.min(allRows.length, MAX_ROWS_TO_SCAN);

  for (let i = startIndex; i < rowsToScan && selectedRows.length < maxRows; i++) {
    const row = allRows[i];
    if (!isRowValid(row)) {
      continue;
    }

    const rowFilledColumns = getFilledColumnIndices(row);
    let addsNewCoverage = false;

    for (const colIndex of rowFilledColumns) {
      if (!coveredColumns.has(colIndex)) {
        addsNewCoverage = true;
        break;
      }
    }

    if (addsNewCoverage || coveredColumns.size < columnCount) {
      selectedRows.push(row);
      for (const colIndex of rowFilledColumns) {
        coveredColumns.add(colIndex);
      }
    }

    if (coveredColumns.size >= columnCount && selectedRows.length >= maxRows) {
      break;
    }
  }

  if (selectedRows.length < maxRows) {
    for (let i = startIndex; i < rowsToScan && selectedRows.length < maxRows; i++) {
      const row = allRows[i];
      if (isRowValid(row) && !selectedRows.includes(row)) {
        selectedRows.push(row);
      }
    }
  }

  return selectedRows;
}

// Should be customized based on real-life examples
function skipFirstLine(rows) {
  return (
    rows.length > 1 &&
    rows[0].every((field) => typeof field === "string") &&
    rows[0].map((field) => field.toLowerCase()).some((field) => /mail/.test(field)) &&
    rows[1].some((field) => typeof field === "string" && field.includes("@"))
  );
}

function isExcelSpreadsheet(file) {
  return (
    file.name.endsWith(".xlsx") ||
    file.name.endsWith(".xls") ||
    file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

function parseExcelSpreadsheet({ file, delimiter: _delimiter }, onComplete) {
  if (!window.Worker) {
    window.showToast("Cannot process xlsx file using this browser", "error");

    onComplete({
      type: ImportFileType.Excel,
      previewRows: [],
      skipFirstLine: false,
      noOfRows: 0,
      delimiter: Delimiters.Comma,
    });
  }

  // xlsx.worker.js is a bundled xls-worker.js file with SheetJS library inside - it has to be served locally
  // because Web Workers do not support cross-origin requests.
  // It could be also handled differently (synchronously) if excel file is smaller than x MB.
  const worker = new Worker("/assets/js/import-v2/xlsx.worker.js");
  worker.postMessage(file);

  worker.onmessage = function (e) {
    const rows = e.data;
    const hasHeader = skipFirstLine(rows);
    const previewRows = selectRowsWithMaxColumnCoverage(rows, ROWS_TO_PREVIEW, hasHeader);

    onComplete({
      type: ImportFileType.Excel,
      previewRows: previewRows,
      skipFirstLine: hasHeader,
      noOfRows: rows.length,
      delimiter: Delimiters.Comma,
    });
  };
}

function parseCSVFile({ file, delimiter }, onComplete) {
  Papa.parse(file, {
    delimiter,
    skipEmptyLines: true,
    complete: function (results) {
      const hasHeader = skipFirstLine(results.data);
      const previewRows = selectRowsWithMaxColumnCoverage(results.data, ROWS_TO_PREVIEW, hasHeader);

      onComplete({
        type: ImportFileType.CSV,
        previewRows,
        skipFirstLine: hasHeader,
        noOfRows: results.data.length,
        delimiter: results.meta.delimiter,
      });
    },
  });
}

export function parseFile({ file, delimiter }, onComplete) {
  if (isExcelSpreadsheet(file)) {
    parseExcelSpreadsheet({ file, delimiter }, onComplete);
  } else {
    parseCSVFile({ file, delimiter }, onComplete);
  }
}
